import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Menu, X, LogOut, Sun, Moon, Globe, ChevronRight, LayoutDashboard } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { DesktopNav } from "@/shared/components/navigation/DesktopNav";
import { MobileNav } from "@/shared/components/navigation/MobileNav";
import { useIsMobile } from "@/shared/hooks/use-mobile";
import { LanguageSwitcher } from "@/shared/components/ui/language-switcher";
import { useTranslation } from "react-i18next";
// DashAI imports Radix Avatar + ScrollArea, dragging ~150 KB of Radix
// chunks into the eager landing bundle. Lazy-load: the chat panel only
// mounts when the user explicitly opens it from the navbar button.
import { SOLUTIONS_URL_FROM_PLATFORM, buildAgentUrl } from "@/shared/lib/solutionsUrl";
const DashAI = lazy(() => import("@/shared/components/full-site/DashAI"));
import MiDashAnimatedLogomark from "@/shared/assets/logos/MiDashAnimatedLogomark";
import { tokenManager } from "@/shared/services/tokenManager";
// Lightweight wrapper — see @/shared/utils/track for rationale. Keeps full
// analytics module out of the eager bundle that ships on initial page load.
import { trackClick } from "@/shared/utils/track";

interface NavbarProps {
  onJoinBeta?: () => void;
}

const Navbar = ({ onJoinBeta }: NavbarProps = {}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDashOpen, setIsDashOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  // Light/dark theme is signaled by the `light` class on <html> OR by a
  // `data-theme="light"` wrapper anywhere up to <body> (e.g. the new
  // landing page's `.midash-landing-new[data-theme="light"]`). We watch
  // both so the user-avatar dropdown and any other theme-aware bits in
  // Navbar re-render when the user navigates between dark and light
  // routes without remounting the Navbar.
  // SSR-safe: returns `true` (brand-default light) when document is missing.
  // The inline <script> in index.html applies the .light class to <html>
  // before this component renders, so reading it during render is safe in
  // the browser. Returning `true` as the SSR fallback matches the
  // theme-bootstrap script's behavior — keeps server/client first-render
  // identical for hydration.
  const computeIsLight = () => {
    if (typeof document === 'undefined') return true;
    return (
      document.documentElement.classList.contains('light') ||
      !!document.querySelector('[data-theme="light"]')
    );
  };
  const [isLight, setIsLight] = useState(computeIsLight);
  useEffect(() => {
    const update = () => setIsLight(computeIsLight());
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-theme', 'class'] });
    return () => observer.disconnect();
  }, []);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t, i18n } = useTranslation();

  // Check auth status on mount and when tokens change
  useEffect(() => {
    const checkAuthStatus = () => {
      const token = tokenManager.getAccessToken();
      const isValid = token && !tokenManager.isTokenExpired();
      setIsLoggedIn(!!isValid);

      if (isValid) {
        const email = localStorage.getItem('userEmail');
        setUserEmail(email);
      } else {
        setUserEmail(null);
      }
    };

    checkAuthStatus();

    // Listen for auth changes
    const handleTokenExpired = () => {
      setIsLoggedIn(false);
      setUserEmail(null);
    };

    const handleLogout = () => {
      setIsLoggedIn(false);
      setUserEmail(null);
    };

    // Listen for localStorage changes (works across tabs too)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'dashAccessToken' || e.key === 'authToken') {
        checkAuthStatus();
      }
    };

    window.addEventListener('tokenExpired', handleTokenExpired);
    window.addEventListener('userLogout', handleLogout);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('tokenExpired', handleTokenExpired);
      window.removeEventListener('userLogout', handleLogout);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Click outside handler for user menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  useEffect(() => {
    const updateTheme = () => {
      setIsLight(document.documentElement.classList.contains('light'));
    };

    updateTheme();
    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const handleLogout = () => {
    tokenManager.logout();
    setIsLoggedIn(false);
    setUserEmail(null);
    setShowUserMenu(false);
    navigate('/');
  };

  const navItems = [
    {
      label: t("nav.platform"),
      subItems: [
        { label: t("nav.platformDropdown.conversationalTrading"), sectionId: "conversation-workflow", description: t("nav.platformDropdown.conversationalTradingDesc") },
        { label: t("nav.platformDropdown.aiTradingAgents"), path: "/products/ai-trading-agents", description: t("nav.platformDropdown.aiTradingAgentsDesc") },
        { label: t("nav.platformDropdown.features"), sectionId: "features", description: t("nav.platformDropdown.featuresDesc") },
        { label: t("nav.platformDropdown.coverage"), path: "/coverage", description: t("nav.platformDropdown.coverageDesc") },
      ]
    },
    {
      label: t("nav.origin", "Origin"),
      subItems: [
        { label: t("nav.originDropdown.story", "Story"), path: "/story", description: t("nav.originDropdown.storyDesc", "Why we built MiDash") },
        { label: t("nav.originDropdown.team", "Team"), path: "/team", description: t("nav.originDropdown.teamDesc", "Meet the people behind MiDash") },
      ]
    },
    {
      label: t("nav.solutions"),
      subItems: [
        { label: t("nav.solutionsDropdown.allSolutions"), path: "/solutions", description: t("nav.solutionsDropdown.allSolutionsDesc") },
        { label: t("nav.solutionsDropdown.institutional"), external: SOLUTIONS_URL_FROM_PLATFORM, description: t("nav.solutionsDropdown.institutionalDesc") },
        // "Pricing" → /plans removed while registration is closed: the route
        // now redirects to /, so linking to it from the nav would be a
        // confusing bounce. Restore this line when signups reopen.
        { label: t("nav.solutionsDropdown.buildAgent", "Build Your Trading Agent"), external: buildAgentUrl("navbar"), description: t("nav.solutionsDropdown.buildAgentDesc", "Have a custom trading agent built for you") },
      ]
    },
    {
      label: t("nav.resources"),
      subItems: [
        { label: t("nav.resourcesDropdown.tradingStrategies"), path: "/strategies", description: t("nav.resourcesDropdown.tradingStrategiesDesc") },
        { label: t("nav.resourcesDropdown.blog"), path: "/resources/blog", description: t("nav.resourcesDropdown.blogDesc") },
        { label: t("nav.resourcesDropdown.faq"), path: "/faq", description: t("nav.resourcesDropdown.faqDesc") },
      ]
    },
    { label: t("nav.contact"), path: "/contact" },
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (!element) {
      return false;
    }

    const offset = 96;
    const top = element.getBoundingClientRect().top + window.scrollY - offset;
    window.history.replaceState(null, "", `#${sectionId}`);
    window.scrollTo({ top, behavior: "smooth" });
    return true;
  };

  const handleNavigation = (item: { path?: string; sectionId?: string; external?: string }) => {
    if (item.external) {
      // External destination (e.g. the Solutions subdomain) — same tab; users
      // can navigate back via the browser.
      window.location.href = item.external;
    } else if (item.path) {
      // Navigate to a route
      navigate(item.path);
    } else if (item.sectionId) {
      // Scroll to section on homepage
      if (location.pathname === "/") {
        const didScroll = scrollToSection(item.sectionId);
        if (!didScroll) {
          navigate(`/#${item.sectionId}`);
        }
      } else {
        navigate(`/#${item.sectionId}`);
      }
    }
  };
  const handleLogin = () => {
    trackClick("Login", "navbar");
    navigate('/login');
  };
  const logoSize = isMobile ? 14 : 22;
  return <header className="fixed w-full top-0 z-50" style={{ height: isMobile ? '70px' : '78px' }}>
      {/* Skip to main content link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 rtl:focus:left-auto rtl:focus:right-2 bg-pivot-primary text-black px-3 py-1 rounded z-[100] focus:outline-none focus:ring-2 focus:ring-pivot-primary"
      >
        {t('common.skipToContent')}
      </a>

      <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/70 to-transparent backdrop-blur-md" />
      <div className="relative z-10 max-w-7xl mx-auto w-full px-3 md:px-5 lg:px-4">
        <div className="flex items-center h-16 gap-3">
          {/* Logo — single SVG matches PNG exactly */}
          <div className="flex items-center shrink-0">
            <Link
              to="/"
              className="shrink-0"
              aria-label="MiDash™ Home"
            >
              <MiDashAnimatedLogomark size={isMobile ? 40 : 50} withText />
            </Link>
          </div>

          <DesktopNav navItems={navItems} onNavigate={handleNavigation} />

          <div className="hidden md:flex items-center gap-4 shrink-0">
            <LanguageSwitcher />

            {isLoggedIn ? (
              <>
                {/* Go to Dashboard Button */}
                <button
                  onClick={() => navigate('/platform')}
                  className="flex items-center gap-2 px-4 py-2 bg-[#6dd8d0]/10 border border-[#6dd8d0]/30 rounded-lg text-[#6dd8d0] hover:bg-[#6dd8d0]/20 transition-all duration-200"
                  style={{ fontFamily: 'var(--font-sans)', fontSize: '14px' }}
                >
                  <LayoutDashboard size={16} />
                  <span>{t('nav.goToDashboard', 'Go to Dashboard')}</span>
                </button>

                {/* User Avatar with Dropdown */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    type="button"
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm text-black transition-all duration-200 hover:scale-105 hover:shadow-lg hover:shadow-[#6dd8d0]/20 focus:outline-none focus:ring-2 focus:ring-[#6dd8d0]/50 focus:ring-offset-2 focus:ring-offset-transparent"
                    style={{ background: 'linear-gradient(135deg, #6dd8d0 0%, #4fc3b8 100%)' }}
                    aria-label={t('settings.preferences', 'Preferences')}
                  >
                    {userEmail ? userEmail[0].toUpperCase() : 'U'}
                  </button>

                  {/* User Dropdown Menu */}
                  {showUserMenu && (
                    <div
                      className={
                        isLight
                          ? "absolute right-0 mt-2 w-64 bg-white/90 border border-black/10 rounded-xl shadow-2xl overflow-hidden z-50"
                          : "absolute right-0 mt-2 w-64 bg-[#0F1114] border border-gray-800 rounded-xl shadow-2xl overflow-hidden z-50"
                      }
                    >
                      {/* User Info Header */}
                      <div className={isLight ? "p-4 border-b border-black/10" : "p-4 border-b border-gray-800"}>
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm text-black"
                            style={{ background: 'linear-gradient(135deg, #6dd8d0 0%, #4fc3b8 100%)' }}
                          >
                            {userEmail ? userEmail[0].toUpperCase() : 'U'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={isLight ? "text-sm font-medium text-black truncate" : "text-sm font-medium text-white truncate"}>
                              {userEmail ? userEmail.split('@')[0] : 'User'}
                            </p>
                            <p className={isLight ? "text-xs text-gray-600 truncate" : "text-xs text-gray-400 truncate"}>
                              {userEmail || 'user@midash.ai'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="p-2">
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            navigate('/platform');
                          }}
                          className={
                            isLight
                              ? "w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:text-black hover:bg-black/5 rounded-lg transition-colors"
                              : "w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                          }
                        >
                          <LayoutDashboard size={16} />
                          <span>{t('nav.dashboard', 'Dashboard')}</span>
                        </button>

                        <button
                          onClick={() => {
                            const newLang = i18n.language === 'en' ? 'ar' : 'en';
                            i18n.changeLanguage(newLang);
                          }}
                          className={
                            isLight
                              ? "w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:text-black hover:bg-black/5 rounded-lg transition-colors"
                              : "w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                          }
                        >
                          <Globe size={16} />
                          <span className="flex-1">{t('settings.language', 'Language')}</span>
                          <span className={isLight ? "text-gray-500 text-xs" : "text-gray-500 text-xs"}>
                            {i18n.language === 'en' ? 'English' : 'العربية'}
                          </span>
                        </button>

                        <div className={isLight ? "my-2 border-t border-black/10" : "my-2 border-t border-gray-800"}></div>

                        <button
                          onClick={handleLogout}
                          className={
                            isLight
                              ? "w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-500/10 rounded-lg transition-colors"
                              : "w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                          }
                        >
                          <LogOut size={16} />
                          <span>{t('settings.logout', 'Logout')}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <button
                onClick={handleLogin}
                className="text-white hover:text-[#6dd8d0] hover:bg-transparent font-normal py-2 tracking-[-0.011em] transition-colors"
                style={{ fontFamily: 'var(--font-sans)', fontSize: '18px' }}
              >
                {t('common.login')}
              </button>
            )}
          </div>

          <div className="flex items-center gap-3 ml-auto md:hidden">
            <div className="relative z-10">
              <LanguageSwitcher />
            </div>
            <button className="relative z-10 text-white p-2 hover:bg-white/5 rounded-lg transition-colors" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label={isMenuOpen ? t("nav.closeMenu") : t("nav.openMenu")}>
              {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      <MobileNav
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        onLogin={handleLogin}
        onLogout={handleLogout}
        onJoinBeta={onJoinBeta}
        navItems={navItems}
        onNavigate={handleNavigation}
        isLoggedIn={isLoggedIn}
        userEmail={userEmail}
      />

      {/* Conditionally render DashAI component (lazy-loaded chat panel) */}
      {isDashOpen && (
        <Suspense fallback={null}>
          <DashAI onClose={() => setIsDashOpen(false)} />
        </Suspense>
      )}
    </header>;
};
export default Navbar;
